import { PageError } from '@/components/PageError'
import { PageLoader } from '@/components/PageLoader'
import {
  useCreditLimitReview,
  useSingleClrTask,
} from '@/hooks/queries/credit/useCreditLimitReviews'
import { createFileRoute } from '@tanstack/react-router'
import { InboxDetail } from '../inbox/-components/InboxDetail'
import { CREDIT_ROUTES, toStaticData } from '../credit-route-config'

export const Route = createFileRoute('/_auth/_modules/credit/credit-limit-reviews/$reviewId')({
  staticData: toStaticData(CREDIT_ROUTES['/credit/credit-limit-reviews/$reviewId']),
  component: RouteComponent,
})

/**
 * One credit limit review, full width — the same detail the inbox shows in its right pane, reached
 * from the CLR table instead of from your queue.
 *
 * It renders `InboxDetail` rather than reproducing it. The pane is ten cards deep and its rules
 * about stage, rework and what counts as resolved are subtle enough that a second implementation
 * would drift within a release; sharing the component makes that impossible by construction.
 *
 * The two surfaces answer different questions about authority, though. The inbox only ever lists
 * requests you are on the chain for, so a live task there is one you may act on. This page is open
 * to anyone holding `credit_limit_reviews_view`, so it passes the viewer's real capabilities and
 * the decision footer appears only for whoever actually holds the pending slot. Everyone else gets
 * the same page to read.
 */
function RouteComponent() {
  const { reviewId } = Route.useParams()
  const id = Number(reviewId)

  const taskQuery = useSingleClrTask(id)
  // Already in flight from the detail cards below, so this costs no extra request — it is read here
  // for `viewer`, which the inbox task shape does not carry.
  const detailQuery = useCreditLimitReview(id)

  if (taskQuery.isPending || detailQuery.isPending) return <PageLoader />
  if (taskQuery.error || !taskQuery.data) return <PageError />

  const viewer = detailQuery.data?.viewer

  return (
    <InboxDetail
      task={taskQuery.data}
      layout="page"
      canAct={{ decide: viewer?.canAct ?? false, rework: viewer?.canResubmit ?? false }}
      // No `onDecided`: unlike the inbox, where deciding empties the row you were on and the queue
      // hands you the next one, this page is about one review and stays on it. The decision's
      // invalidations refetch the task and the viewer, so the footer comes off and the resolved
      // banner takes its place around the reader.
    />
  )
}
